/**
 * 客服实时通道客户端（连接 /support 命名空间）
 * 参考现有 SocketService 的连接与重连机制，独立于 AI 对话通道。
 */

import { io, Socket } from 'socket.io-client';
import { getSocketConfig } from '../config/socket';
import type { SupportMessage } from './supportApi';

type MessageHandler = (msg: SupportMessage) => void;
type HistoryHandler = (data: { conversationId: string; messages: SupportMessage[] }) => void;
type ErrorHandler = (err: { code: string; message: string }) => void;
type JoinedHandler = (data: { conversationId: string }) => void;

class SupportSocketClient {
  private socket: Socket | null = null;

  private resolveUrl(): string {
    let url = import.meta.env.VITE_SOCKET_URL || '/';
    if (url === '/') url = window.location.origin;
    return url;
  }

  connect(): void {
    if (this.socket?.connected) return;

    const token = localStorage.getItem('auth_token') || '';
    const config = getSocketConfig();

    // 连接 /support 命名空间
    this.socket = io(`${this.resolveUrl()}/support`, {
      ...config,
      autoConnect: false,
      auth: { token },
    });

    this.socket.connect();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  join(conversationId?: string): void {
    this.socket?.emit('support:join', { conversationId });
  }

  requestHistory(conversationId: string, before?: string, limit = 50): void {
    this.socket?.emit('support:history', { conversationId, before, limit });
  }

  sendText(conversationId: string, content: string): void {
    this.socket?.emit('support:send', { conversationId, contentType: 'text', content });
  }

  sendImage(conversationId: string, imageUrl: string): void {
    this.socket?.emit('support:send', { conversationId, contentType: 'image', imageUrl });
  }

  onMessage(handler: MessageHandler): void {
    this.socket?.on('support:message', handler);
  }

  onHistory(handler: HistoryHandler): void {
    this.socket?.on('support:history', handler);
  }

  onJoined(handler: JoinedHandler): void {
    this.socket?.on('support:joined', handler);
  }

  onError(handler: ErrorHandler): void {
    this.socket?.on('support:error', handler);
  }

  onConnect(handler: () => void): void {
    this.socket?.on('connect', handler);
  }
}

export const supportSocket = new SupportSocketClient();
export default SupportSocketClient;
